'use client';
import type { Stock3D } from '@/lib/scatter3d-geometry';
import { compositeScore } from '@/lib/composite-score';

type TierFilter = 'all' | 'alpha' | 'top10q' | 'top20q' | 'top15q' | 'combined';

/** First-word brand from a company name (saves width in compact lists).
 *  "Jones Lang LaSalle Inc." → "Jones". "PDD Holdings" → "PDD".
 *  Strips trailing corporate suffixes first so they don't pollute the split. */
function firstWord(name: string | undefined | null): string {
  if (!name) return '';
  const cleaned = name
    .replace(
      /[,.]?\s+(Inc\.?|Incorporated|Corp\.?|Corporation|Co\.?|Company|Ltd\.?|Limited|Plc\.?|S\.A\.?|AG|NV|LLC|Holdings?|Group|Class\s+[AB])\.?$/i,
      '',
    )
    .trim();
  return (cleaned.split(/\s+/)[0] ?? name).slice(0, 14);
}

/**
 * Floating list of stocks in the active tier filter (Alpha/Top10Q/Top20Q).
 * Self-contained presentational component — only depends on which symbols are
 * in the picked tier and an onClick handler from the parent.
 */
export function TierStockList({
  tierFilter,
  stocks3d,
  tierPicks,
  selectedSymbol,
  onSelectStock,
}: {
  tierFilter: TierFilter;
  stocks3d: Stock3D[];
  tierPicks: Set<string>;
  selectedSymbol: string | null;
  onSelectStock: (symbol: string) => void;
}) {
  // 'combined' is the Portfolio Visualizer's super-planet mode — the parent
  // has already collapsed plotStocks to one synthetic position, so the list
  // would just show that one synthetic row which is useless. Hide it.
  if (tierFilter === 'all' || tierFilter === 'combined') return null;

  const accent =
    tierFilter === 'alpha' ? '#22c55e' :
    tierFilter.startsWith('top') ? '#f59e0b' : '#60a5fa';

  const heading =
    tierFilter === 'alpha' ? 'Alpha Zone — Top 10' :
    tierFilter === 'top10q' ? 'Top 10 Quality' :
    tierFilter === 'top15q' ? 'Top 15 Quality' :
    tierFilter === 'top20q' ? 'Top 20 Quality' :
    'All';

  return (
    <div
      className="absolute bottom-10 left-3 z-10 font-mono text-[9px] rounded-lg overflow-hidden"
      style={{
        background: 'rgba(10, 12, 20, 0.92)',
        border: `1px solid ${accent}30`,
        maxHeight: 460,
        overflowY: 'auto',
      }}
    >
      <div
        className="px-2 py-1 font-semibold text-[10px] sticky top-0 leading-tight"
        style={{
          background: 'rgba(10, 12, 20, 0.98)',
          color: accent,
          borderBottom: '1px solid #1e293b',
        }}
      >
        <div className="flex items-center justify-between gap-2">
          <span>{heading}</span>
          <span className="flex gap-1.5 text-[8px] text-[#64748b] font-normal">
            <span style={{ width: 18, textAlign: 'right' }}>Q</span>
            <span style={{ width: 18, textAlign: 'right' }}>V</span>
            <span style={{ width: 18, textAlign: 'right' }}>M</span>
          </span>
        </div>
      </div>
      {[...stocks3d]
        .filter(s => tierPicks.has(s.symbol))
        // Canonical alpha-basket sort: composite descending. Same order on
        // every surface that lists the basket (TierStockList here,
        // AlphaZoneHoldingsTable, alpha-zone-tracker per-stock table).
        .sort((a, b) => (compositeScore(b.quality, b.valuation) ?? -Infinity) - (compositeScore(a.quality, a.valuation) ?? -Infinity))
        .map(s => (
          <div
            key={s.symbol}
            className="px-2 py-[2px] flex items-center gap-1.5 cursor-pointer hover:bg-white/5 leading-tight"
            onClick={() => onSelectStock(s.symbol)}
            style={{
              borderBottom: '1px solid #0f1525',
              background: selectedSymbol === s.symbol ? 'rgba(255,255,255,0.06)' : undefined,
            }}
          >
            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
            <span className="text-white font-semibold" style={{ minWidth: 42 }}>{s.symbol}</span>
            <span className="text-[#cbd5e1] truncate" style={{ width: 76 }}>{firstWord(s.name)}</span>
            <span className="text-[8px] flex-shrink-0 truncate" style={{ width: 64, color: s.color, opacity: 0.75 }}>{s.sector}</span>
            <span className="flex gap-1.5 text-[8px] flex-shrink-0 ml-auto">
              <span style={{ width: 18, textAlign: 'right', color: s.quality > 0 ? '#4ade80' : '#f87171' }}>{s.quality.toFixed(1)}</span>
              <span style={{ width: 18, textAlign: 'right', color: s.valuation > 0 ? '#4ade80' : '#f87171' }}>{s.valuation.toFixed(1)}</span>
              <span style={{ width: 18, textAlign: 'right', color: s.technical > 0 ? '#4ade80' : '#f87171' }}>{s.technical.toFixed(1)}</span>
            </span>
          </div>
        ))}
    </div>
  );
}
